import { useQuery } from '@tanstack/react-query'
import { ArrowRight, Clock, HandCoins } from 'lucide-react'
import { townLine } from '../../lib/address-place'
import { orgHref } from '../../lib/public-router'
import { useTRPC } from '../../lib/trpc'
import { Notice } from '../../ui'
import { SiteShell } from '../../ui/SiteShell'
import { PRODUCT_NAV } from './product-nav'
import './marketing.css'

/**
 * The congregations publishing here.
 *
 * The marketing page can describe the product; only a real shul's own pages
 * show it running for somebody. Every claim the front door makes about the
 * schedule is checkable from here in two clicks, which is the whole reason this
 * page exists rather than a carousel of screenshots.
 *
 * It lists what `org.directory` returns — a public read that decides for itself
 * which congregations belong in a directory — and links each one at its ROOT
 * rather than at a named page, so an org that gains a landing page later is
 * reached correctly with no change here.
 */
export function ShulsPage({ onLogin }: { onLogin?: () => void }) {
  const trpc = useTRPC()
  const orgs = useQuery(trpc.org.directory.queryOptions())

  const shuls = orgs.data ?? []

  return (
    <SiteShell nav={PRODUCT_NAV} onLogin={onLogin}>
      <section className="mk-section mk-section--first">
        <div className="mk-measure">
          <header className="mk-section__head">
            <p className="mk-eyebrow">Congregations</p>
            <h1 className="mk-section__title">
              Shuls publishing their schedule here
            </h1>
            <p className="mk-section__lede">
              Every one of these pages is live and computed this morning. Open
              any of them to see what your own would look like.
            </p>
          </header>

          {orgs.isLoading ? <Notice>Loading…</Notice> : null}
          {orgs.isError ? (
            <Notice tone="error">The directory could not be loaded.</Notice>
          ) : null}
          {!orgs.isLoading && !orgs.isError && shuls.length === 0 ? (
            <Notice>No congregations are publishing yet.</Notice>
          ) : null}

          <div className="mk-dir">
            {shuls.map((s) => (
              <a
                className="mk-dir__row"
                key={s.shortId}
                href={orgHref(s.shortId, '')}
              >
                <span className="mk-dir__names">
                  <span className="mk-dir__name">{s.name}</span>
                  {/* The pair takes ONE family — Plex carries both scripts, so a
                      name beside its Hebrew is not split across two faces. */}
                  {s.nameHe ? (
                    <span className="mk-dir__name-he" dir="rtl">
                      {s.nameHe}
                    </span>
                  ) : null}
                </span>

                <span className="mk-dir__where">
                  {townLine({
                    city: s.city,
                    region: s.region,
                    country: s.country,
                  })}
                </span>

                {/* What the shul publishes, stated rather than promised — donate
                    appears only where money would actually go through. */}
                <span className="mk-dir__pages">
                  <span className="mk-dir__page">
                    <Clock className="mk-dir__icon" aria-hidden />
                    Zmanim
                  </span>
                  {s.acceptsDonations ? (
                    <span className="mk-dir__page">
                      <HandCoins className="mk-dir__icon" aria-hidden />
                      Donate
                    </span>
                  ) : null}
                </span>

                <ArrowRight className="mk-dir__go" aria-hidden />
              </a>
            ))}
          </div>
        </div>
      </section>
    </SiteShell>
  )
}
